import { infiniteQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { Deck, PublicCourseListResponse, PublicDeckListResponse } from '../types'

export function usePublicDecks(page = 1, pageSize = 20, search?: string) {
  return useQuery({
    queryKey: ['public', 'decks', page, pageSize, search ?? ''],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (search) params.set('q', search)  // backend expects ?q=
      const { data } = await api.get<PublicDeckListResponse>(`/api/v1/public/decks?${params}`)
      return data
    },
  })
}

export function usePublicCourses(page = 1, pageSize = 20, search?: string) {
  return useQuery({
    queryKey: ['public', 'courses', page, pageSize, search ?? ''],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (search) params.set('q', search)  // backend expects ?q=
      const { data } = await api.get<PublicCourseListResponse>(`/api/v1/public/courses?${params}`)
      return data
    },
  })
}

// ── Infinite-query variants for Explore's load-more feed ──────────────────────

/** Shared with the neighbour prefetch; `abortable` is for the prefetch only (see useProgress). */
export const publicDecksInfiniteQuery = (pageSize = 20, search?: string, excludeSystem = false, abortable = false) =>
  infiniteQueryOptions({
    queryKey: ['public', 'decks', 'infinite', pageSize, search ?? '', excludeSystem],
    initialPageParam: 1,
    queryFn: async (ctx) => {
      const params = new URLSearchParams({
        page: String(ctx.pageParam),
        page_size: String(pageSize),
        exclude_system: String(excludeSystem),
      })
      if (search) params.set('q', search)
      const { data } = await api.get<PublicDeckListResponse>(`/api/v1/public/decks?${params}`, abortable ? { signal: ctx.signal } : undefined)
      return data
    },
    getNextPageParam: (lastPage: PublicDeckListResponse, allPages: PublicDeckListResponse[]) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
    // 30 s, as ['decks'] (see useDecks). Local changes invalidate ['public'];
    // a deck someone else has just published appears up to 30 s late.
    staleTime: 30_000,
  })

export function usePublicDecksInfinite(pageSize = 20, search?: string, excludeSystem = false) {
  return useInfiniteQuery(publicDecksInfiniteQuery(pageSize, search, excludeSystem))
}

export function usePublicCoursesInfinite(pageSize = 20, search?: string) {
  return useInfiniteQuery({
    queryKey: ['public', 'courses', 'infinite', pageSize, search ?? ''],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ page: String(pageParam), page_size: String(pageSize) })
      if (search) params.set('q', search)
      const { data } = await api.get<PublicCourseListResponse>(`/api/v1/public/courses?${params}`)
      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0)
      return loaded < lastPage.total ? allPages.length + 1 : undefined
    },
  })
}

export function useCopyDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ deckId, isActiveForLearning = true }: { deckId: string; isActiveForLearning?: boolean }) => {
      const { data } = await api.post<Deck>(
        `/api/v1/public/decks/${deckId}/copy`,
        { is_active_for_learning: isActiveForLearning },
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['public'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}

export function useUnenrollDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (deckId: string) => {
      await api.delete(`/api/v1/public/decks/${deckId}/enroll`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public'] })
    },
  })
}
